import { FeatureVideoHelp } from "../../../components/ui/FeatureVideoHelp";
import i18n from "../../../i18n";import { Trans } from "react-i18next";import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Check } from "lucide-react";
import { cn } from "../../../lib/utils";
import { Button } from "../../../components/ui/button";
import { LeaveDateField } from "../components/LeaveDateField";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue } from
"../../../components/ui/select";
import { FormPage } from "../../../components/layout/FormPage";
import { AppLayout, PageBackLink } from "../../../components/layout";
import { AlertBanner, ContentCard, PageHeader, ui } from "../../../components/ui";
import { Textarea } from "../../../components/ui/textarea";
import { BalanceDurationBar, LeaveTypeSelect } from "../components";
import { leaveService, generateLeavesDetail, checkLeaveOverlap } from "../../../services/leaveService";
import { formatLeaveValidationError } from "../../../lib/leaveValidationErrors";
import sessionDB from "../../../utils/sessionDB";
import { extractLeaveRecords } from "../../../lib/leaveRecords";
import { getAvailableLeaveBalanceForType } from "../../../lib/leaveBalance";
import { validateLeaveDatesForHolidays } from "../../../lib/leaveHolidayValidation";

export function ApplyLeavePage() {
  const navigate = useNavigate();
  const [fromDate, setFromDate] = useState(undefined);
  const [toDate, setToDate] = useState(undefined);
  const [fromSession, setFromSession] = useState("");
  const [toSession, setToSession] = useState("");
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [selectedTypeId, setSelectedTypeId] = useState("");
  const [reason, setReason] = useState("");

  const [loading, setLoading] = useState(false);
  const [loadingTypes, setLoadingTypes] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [toast, setToast] = useState(null);

  const [existingRequests, setExistingRequests] = useState([]);
  const [leaveBalances, setLeaveBalances] = useState([]);
  const [submitted, setSubmitted] = useState(false);

  // Load Leave Types from local service
  useEffect(() => {
    setErrorMsg("");
    setLoadingTypes(true);
    leaveService.getLeaveTypes()
    .then((data) => {
      setLeaveTypes(data);
    })
    .catch((err) => {
      console.error("Error loading leave types:", err);
      setErrorMsg("Failed to load leave types.");
    })
    .finally(() => {
      setLoadingTypes(false);
    });
  }, []);

  // Load existing leave requests and balances for the logged-in employee
  useEffect(() => {
    const sessionEmpId = Number(sessionDB.getEmpId());
    const optionalEmpId =
    Number.isFinite(sessionEmpId) && sessionEmpId > 0 ? sessionEmpId : undefined;

    setExistingRequests([]);
    leaveService.getEmployeeLeaveRequests(optionalEmpId)
    .then((response) => {
      setExistingRequests(extractLeaveRecords(response));
    })
    .catch((err) => {
      console.error("Error loading employee requests:", err);
    });

    leaveService.getLeaveBalance(optionalEmpId)
    .then(setLeaveBalances)
    .catch((err) => {
      console.error("Error loading leave balances:", err);
      setLeaveBalances([]);
    });
  }, []);

  const selectedType = useMemo(() => {
    return leaveTypes.find((lt) => String(lt.typeId) === selectedTypeId);
  }, [leaveTypes, selectedTypeId]);

  const availableBalance = useMemo(() => {
    return getAvailableLeaveBalanceForType(selectedType, leaveBalances);
  }, [selectedType, leaveBalances]);

  const duration = useMemo(() => {
    if (!fromDate || !toDate) return null;
    const start = new Date(fromDate);
    start.setHours(0, 0, 0, 0);
    const end = new Date(toDate);
    end.setHours(0, 0, 0, 0);

    if (end < start) {
      return "Invalid Combination";
    }

    if (start.getTime() === end.getTime()) {
      if (fromSession === "Second Half" && toSession === "First Half") {
        return "Invalid Combination";
      }
      if (fromSession === "First Half" && toSession === "First Half") {
        return 0.5;
      }
      if (fromSession === "Second Half" && toSession === "Second Half") {
        return 0.5;
      }
      if (fromSession === "First Half" && toSession === "Second Half") {
        return 1.0;
      }
    } else {
      if (!fromSession || !toSession) return null;
      const diffTime = Math.abs(end.getTime() - start.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      const fromSessions = fromSession === "First Half" ? 2 : fromSession === "Second Half" ? 1 : 0;
      const toSessions = toSession === "First Half" ? 1 : toSession === "Second Half" ? 2 : 0;

      const intermediateSessions = (diffDays - 1) * 2;
      const totalSessions = fromSessions + toSessions + intermediateSessions;
      return totalSessions * 0.5;
    }
    return null;
  }, [fromDate, fromSession, toDate, toSession]);

  const newLeavesDetail = useMemo(() => {
    if (!fromDate || !toDate || !fromSession || !toSession || duration === "Invalid Combination") {
      return null;
    }
    return generateLeavesDetail(fromDate, toDate, fromSession, toSession);
  }, [fromDate, toDate, fromSession, toSession, duration]);

  const balanceError = useMemo(() => {
    if (!selectedType || availableBalance === null || typeof duration !== "number") return null;
    if (duration > availableBalance) {
      return `Insufficient leave balance. Available balance is ${availableBalance.toFixed(1)} ${availableBalance === 1 ? "day" : "days"}.`;
    }
    return null;
  }, [selectedType, availableBalance, duration]);

  const overlapError = useMemo(() => {
    if (!newLeavesDetail || !existingRequests || existingRequests.length === 0) return null;
    return checkLeaveOverlap(newLeavesDetail, existingRequests);
  }, [newLeavesDetail, existingRequests]);

  // Field validation checks
  const fieldErrors = useMemo(() => {
    const errors = {};
    if (submitted) {
      if (!selectedTypeId) errors.leaveType = "Leave Type is required.";
      if (!fromDate) errors.fromDate = "From Date is required.";
      if (!fromSession) errors.fromSession = "From Session is required.";
      if (!toDate) errors.toDate = "To Date is required.";
      if (!toSession) errors.toSession = "To Session is required.";
      if (!reason.trim()) errors.reason = "Reason is required.";

      if (fromDate && toDate) {
        const start = new Date(fromDate);
        start.setHours(0, 0, 0, 0);
        const end = new Date(toDate);
        end.setHours(0, 0, 0, 0);
        if (end < start) {
          errors.toDate = "To Date cannot be before From Date.";
        } else if (start.getTime() === end.getTime() && fromSession === "Second Half" && toSession === "First Half") {
          errors.toSession = "Second Half → First Half is an invalid combination.";
        }
      }
    }
    return errors;
  }, [submitted, selectedTypeId, fromDate, fromSession, toDate, toSession, reason]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitted(true);

    if (!selectedTypeId || !fromDate || !fromSession || !toDate || !toSession || !reason.trim()) {
      setErrorMsg("Please fill out all required fields.");
      return;
    }

    if (duration === "Invalid Combination") {
      setErrorMsg("Invalid date or session combination.");
      return;
    }

    if (balanceError || overlapError) {
      setErrorMsg(balanceError || overlapError);
      return;
    }

    const empId = sessionDB.getEmpId();
    if (!empId) {
      setErrorMsg("Unable to determine the logged-in employee from the login session.");
      return;
    }

    setErrorMsg("");
    setLoading(true);

    try {
      const holidayCheck = await validateLeaveDatesForHolidays(fromDate, toDate);
      if (!holidayCheck.valid) {


        //  setErrorMsg(holidayCheck.message || "Requested leave dates overlap with an official public holiday.");
        // return;
      }const formatDateStr = (d) => {
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
      };

      const leavesDetail = newLeavesDetail || generateLeavesDetail(fromDate, toDate, fromSession, toSession);

      const payload = {
        tenantId: sessionDB.getTenantId(),
        empId,
        leaveTypeId: Number(selectedTypeId),
        startDate: formatDateStr(fromDate),
        endDate: formatDateStr(toDate),
        leaveReason: reason,
        leavesDetail
      };

      // Apply leave via API
      await leaveService.applyLeave(payload);

      // Trigger success toast
      setToast({ message: "Leave application submitted successfully.", type: "success" });

      setTimeout(() => {
        setToast(null);
        navigate("/hrms-ess/leave-report");
      }, 1500);

    } catch (err) {
      console.error("Error applying leave:", err);
      setErrorMsg(formatLeaveValidationError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate("/hrms-ess/leave-report");
  };

  return (
    <AppLayout
      overlay={
      toast ?
      <div className="fixed bottom-5 right-5 z-50 animate-in fade-in slide-in-from-bottom-5 duration-300 pointer-events-none">
            <div className="flex items-center gap-2.5 bg-white border border-(--color-text-border)/60 text-foreground px-4 py-3 rounded-2xl shadow-[0_15px_40px_-10px_rgba(30,51,131,0.25)] font-sans text-sm font-medium">
              <div className="h-6 w-6 rounded-full bg-success/10 text-success grid place-items-center">
                <Check className="h-4 w-4" />
              </div>
              <span>{toast.message}</span>
            </div>
          </div> :
      null
      }>



            <PageHeader inlineControls className="w-full" back={<PageBackLink to="/hrms-ess/leave-report" />} title={i18n.t("formActions.applyLeave")} action={<FeatureVideoHelp title="How to apply for leave" page="ApplyLeave" />} />


          <FormPage size="compact">
            <ContentCard bodyClassName="form-section">
            <form onSubmit={handleSubmit} className="space-y-4">
              {errorMsg && <AlertBanner type="error">⚠️ {errorMsg}</AlertBanner>}

              {/* Leave Type Select */}
              <div className="space-y-1">
                <label className="text-sm font-medium">
                  <Trans i18nKey="leave.leaveType" /> <span className="text-error">*</span>
                </label>
                <LeaveTypeSelect
              leaveTypes={leaveTypes}
              leaveBalances={leaveBalances}
              value={selectedTypeId}
              onValueChange={setSelectedTypeId}
              disabled={loading || loadingTypes}
              loading={loadingTypes}
              error={fieldErrors.leaveType} />

                {fieldErrors.leaveType &&
            <p className="text-xs text-error font-medium mt-1">{fieldErrors.leaveType}</p>
            }
              </div>

              {/* Date & Session Pickers Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 form-grid">
                {/* From Date */}
                <div className="space-y-1">
                  <label className="text-sm font-medium">
                    <Trans i18nKey="dateTime.fromDate" /> <span className="text-error">*</span>
                  </label>
                  <LeaveDateField
                placeholder={i18n.t("dateTime.selectDate2")}
                value={fromDate}
                onChange={setFromDate}
                disabled={loading}
                error={Boolean(fieldErrors.fromDate)}
                dateFormat="PPP" />

                  {fieldErrors.fromDate &&
              <p className="text-xs text-error font-medium mt-1">{fieldErrors.fromDate}</p>
              }
                </div>

                {/* From Session */}
                <div className="space-y-1">
                  <label className="text-sm font-medium">
                    <Trans i18nKey="leave.fromSession" /> <span className="text-error">*</span>
                  </label>
                  <Select value={fromSession} onValueChange={setFromSession} disabled={loading}>
                    <SelectTrigger className={cn("h-11 rounded-xl border-(--color-text-border) bg-white text-sm focus:ring-accent", fieldErrors.fromSession && "border-error")}>
                      <SelectValue placeholder={i18n.t("leave.selectSession2")} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="First Half"><Trans i18nKey="leave.firstHalf" /></SelectItem>
                      <SelectItem value="Second Half"><Trans i18nKey="leave.secondHalf" /></SelectItem>
                    </SelectContent>
                  </Select>
                  {fieldErrors.fromSession &&
              <p className="text-xs text-error font-medium mt-1">{fieldErrors.fromSession}</p>
              }
                </div>

                {/* To Date */}
                <div className="space-y-1">
                  <label className="text-sm font-medium">
                    <Trans i18nKey="dateTime.toDate" /> <span className="text-error">*</span>
                  </label>
                  <LeaveDateField
                placeholder={i18n.t("dateTime.selectDate2")}
                value={toDate}
                onChange={setToDate}
                disabled={loading}
                error={Boolean(fieldErrors.toDate)}
                dateFormat="PPP" />

                  {fieldErrors.toDate &&
              <p className="text-xs text-error font-medium mt-1">{fieldErrors.toDate}</p>
              }
                </div>

                {/* To Session */}
                <div className="space-y-1">
                  <label className="text-sm font-medium">
                    <Trans i18nKey="leave.toSession" /> <span className="text-error">*</span>
                  </label>
                  <Select value={toSession} onValueChange={setToSession} disabled={loading}>
                    <SelectTrigger className={cn("h-11 rounded-xl border-(--color-text-border) bg-white text-sm focus:ring-accent", fieldErrors.toSession && "border-error")}>
                      <SelectValue placeholder={i18n.t("leave.selectSession2")} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="First Half"><Trans i18nKey="leave.firstHalf" /></SelectItem>
                      <SelectItem value="Second Half"><Trans i18nKey="leave.secondHalf" /></SelectItem>
                    </SelectContent>
                  </Select>
                  {fieldErrors.toSession &&
              <p className="text-xs text-error font-medium mt-1">{fieldErrors.toSession}</p>
              }
                </div>
              </div>

              {duration !== null &&
          <BalanceDurationBar
            duration={duration}
            balanceError={balanceError}
            overlapError={overlapError} />

          }

              {/* Reason Textarea */}
              <div className="space-y-1">
                <label className="text-sm font-medium">
                  <Trans i18nKey="common.reason" /> <span className="text-error">*</span>
                </label>
                <Textarea

              value={reason}
              onChange={(e) => setReason(e.target.value)}
              onInput={(e) => {
                e.currentTarget.style.height = "44px";
                e.currentTarget.style.height = `${Math.max(44, e.currentTarget.scrollHeight)}px`;
              }}
              placeholder={i18n.t("leave.enterReasonForLeave2")}
              disabled={loading}
              className={cn(
                "min-h-11 h-11 rounded-xl border-(--color-text-border) bg-white resize-none overflow-hidden px-3 py-2.5 leading-5 text-sm placeholder:text-muted-foreground focus-visible:ring-accent",
                fieldErrors.reason && "border-error"
              )} />
                {fieldErrors.reason &&
            <p className="text-xs text-error font-medium mt-1">{fieldErrors.reason}</p>
            }
              </div>

              {/* Form Buttons */}
              <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-3 pt-2 form-actions">
                <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              disabled={loading}
              className="h-11 rounded-xl px-5 cursor-pointer">
                  <Trans i18nKey="formActions.cancel" />

            </Button>
                <Button
              type="submit"
              disabled={loading}
              className={`${ui.primaryBtn} min-w-[120px]`}>

                  {loading ? "Applying..." : "Apply Leave"}
                </Button>
              </div>
            </form>
          </ContentCard>
          </FormPage>
    </AppLayout>);

}
